import React, { useState, useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import TeachingTooltip from '@/components/TeachingTooltip';
import { useTheme } from '@/context/ThemeContext';

const BaseChangeCalc = () => {
  const { dark } = useTheme();
  const [sOld, setSOld] = useState(30);
  const [vOld, setVOld] = useState(13.8);
  const [sNew, setSNew] = useState(100);
  const [vNew, setVNew] = useState(13.8);
  const [zpuOld, setZpuOld] = useState(0.08);

  const zpuNew = useMemo(() => {
    if (sOld === 0 || vNew === 0) return 0;
    return zpuOld * (sNew / sOld) * Math.pow(vOld / vNew, 2);
  }, [zpuOld, sOld, vOld, sNew, vNew]);

  const ratio = useMemo(() => (sNew / sOld) * Math.pow(vOld / vNew, 2), [sOld, vOld, sNew, vNew]);

  const chartData = [
    { name: 'Old Z_pu', value: parseFloat(zpuOld.toFixed(4)) },
    { name: 'New Z_pu', value: parseFloat(zpuNew.toFixed(4)) },
  ];

  const axisColor = dark ? '#64748b' : '#94a3b8';
  const gridColor = dark ? '#1e293b' : '#f1f5f9';
  const tooltipBg = dark ? '#1e293b' : '#ffffff';
  const tooltipBorder = dark ? '#334155' : '#e2e8f0';

  const NumberInput = ({ label, value, onChange, min, max, step, unit, tooltip }) => (
    <div className="space-y-1.5">
      <div className="flex items-center gap-1">
        <label className="text-xs font-medium text-slate-700 dark:text-slate-300">{label}</label>
        {tooltip && <TeachingTooltip showIcon {...tooltip} />}
      </div>
      <div className="flex items-center gap-1.5">
        <input
          type="number"
          value={value}
          min={min}
          max={max}
          step={step}
          onChange={e => onChange(parseFloat(e.target.value) || 0)}
          className="w-full text-sm border border-slate-300 dark:border-slate-600 rounded-md px-2.5 py-1.5 bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-1 focus:ring-teal-500 focus:border-teal-500 outline-none"
        />
        <span className="text-xs text-slate-500 w-12 flex-shrink-0">{unit}</span>
      </div>
    </div>
  );

  return (
    <div data-testid="base-change-calc" className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        {/* Old base */}
        <div className="bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700 rounded-lg p-4 space-y-3">
          <p className="text-xs font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-500">
            Equipment (Old) Base
          </p>
          <NumberInput
            label="S_base, old (MVA)"
            value={sOld}
            onChange={setSOld}
            min={0.1}
            max={10000}
            step={0.1}
            unit="MVA"
            tooltip={{
              whatThis: "The MVA rating on the equipment's nameplate — the base that the manufacturer used to express %Z.",
              tryThis: "Set this to 30 MVA (a typical distribution transformer) and set the new base to 100 MVA system base.",
              whyMatters: "Equipment from different manufacturers uses different MVA bases. You must convert to a common system base before adding impedances.",
              connectedTo: "→ Updates: conversion ratio and new Z_pu result.",
            }}
          />
          <NumberInput
            label="V_base, old (kV, L-L)"
            value={vOld}
            onChange={setVOld}
            min={0.1}
            max={1000}
            step={0.1}
            unit="kV"
            tooltip={{
              whatThis: "The nominal voltage on the equipment side where the impedance was measured — matches nameplate voltage rating.",
              tryThis: "Keep old and new V_base equal to see that only the S_base ratio matters when voltages are the same.",
              whyMatters: "If voltage zones differ (e.g., you referred impedance to the wrong side), the V² term corrects it.",
              connectedTo: "→ Updates: conversion ratio (V_old/V_new)² term.",
            }}
          />
          <NumberInput
            label="Z_pu, old"
            value={zpuOld}
            onChange={setZpuOld}
            min={0}
            max={100}
            step={0.001}
            unit="pu"
            tooltip={{
              whatThis: "The per-unit impedance of the equipment on its own nameplate base. Transformer nameplate %Z ÷ 100 gives this number.",
              tryThis: "Enter 0.08 for an 8% transformer. Then change the system base to 100 MVA and watch it scale up.",
              whyMatters: "The nameplate per-unit value is useless until it's on the same base as the rest of your system.",
              connectedTo: "→ Multiplied by the conversion ratio to give Z_pu, new.",
            }}
          />
        </div>

        {/* New base */}
        <div className="bg-teal-50 dark:bg-teal-900/20 border border-teal-200 dark:border-teal-800 rounded-lg p-4 space-y-3">
          <p className="text-xs font-semibold uppercase tracking-widest text-teal-700 dark:text-teal-400">
            System (New) Base
          </p>
          <NumberInput
            label="S_base, new (MVA)"
            value={sNew}
            onChange={setSNew}
            min={0.1}
            max={10000}
            step={0.1}
            unit="MVA"
            tooltip={{
              whatThis: "Your chosen system base — typically 100 MVA for large transmission systems, 10 MVA for distribution studies.",
              tryThis: "Try 100 MVA, 50 MVA, and 10 MVA and watch how the ratio S_new/S_old scales the impedance linearly.",
              whyMatters: "Picking a system base equal to one of the major transformers simplifies the calculation — that transformer keeps its nameplate value.",
              connectedTo: "→ Sets the S_new/S_old scaling factor.",
            }}
          />
          <NumberInput
            label="V_base, new (kV, L-L)"
            value={vNew}
            onChange={setVNew}
            min={0.1}
            max={1000}
            step={0.1}
            unit="kV"
            tooltip={{
              whatThis: "The nominal voltage of the bus zone in your per-unit system. Should match the nominal voltage at that bus.",
              tryThis: "Keep V_new = V_old for most cases. Change it only if you're deliberately working in a different voltage zone.",
              whyMatters: "Mismatching the voltage base is the #1 per-unit error on distribution studies.",
              connectedTo: "→ Sets the (V_old/V_new)² scaling factor.",
            }}
          />

          {/* Result */}
          <div className="bg-white dark:bg-slate-900 border border-teal-300 dark:border-teal-700 rounded-lg p-3 mt-2">
            <p className="text-xs font-medium text-teal-700 dark:text-teal-400 uppercase tracking-widest mb-1">
              Z_pu, new
            </p>
            <p data-testid="zpunew-result" className="text-3xl font-bold text-teal-900 dark:text-teal-100 font-mono">
              {zpuNew.toFixed(4)}
            </p>
            <p className="text-xs text-slate-500 dark:text-slate-500 mt-1">
              per-unit on {sNew} MVA, {vNew} kV base
            </p>
            <p className="text-xs text-teal-700 dark:text-teal-400 mt-1.5">
              Conversion ratio: × {ratio.toFixed(4)}
            </p>
          </div>
        </div>
      </div>

      {/* Chart */}
      <div>
        <p className="text-xs font-medium text-slate-600 dark:text-slate-400 mb-2">
          Before vs. After Comparison
        </p>
        <ResponsiveContainer width="100%" height={160}>
          <BarChart data={chartData} margin={{ top: 4, right: 16, bottom: 4, left: 8 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={gridColor} vertical={false} />
            <XAxis dataKey="name" tick={{ fontSize: 11, fill: axisColor }} tickLine={false} axisLine={false} />
            <YAxis
              tick={{ fontSize: 10, fill: axisColor }}
              tickLine={false}
              axisLine={{ stroke: axisColor }}
              tickFormatter={v => v.toFixed(3)}
              domain={[0, 'auto']}
            />
            <Tooltip
              contentStyle={{ background: tooltipBg, border: `1px solid ${tooltipBorder}`, borderRadius: '6px', fontSize: '11px' }}
              formatter={(v) => [v.toFixed(4), 'Z_pu']}
            />
            <Bar dataKey="value" radius={[4, 4, 0, 0]}>
              <Cell fill="#94a3b8" />
              <Cell fill="#0d9488" />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default BaseChangeCalc;
